/**
 * Base local del dispositivo (IndexedDB via Dexie).
 *
 * Se usa IndexedDB y NO `localStorage`: hace falta almacenamiento estructurado,
 * indices, transacciones y una cola. `localStorage` es sincrono, sin indices y con
 * un limite pequeno — no es una base de datos.
 *
 * El esquema esta VERSIONADO. Cada version nueva declara su migracion; Dexie las
 * aplica en orden al abrir. Una actualizacion del service worker no puede perder
 * operaciones pendientes porque el outbox vive aqui, no en memoria.
 */

import Dexie, { type EntityTable } from 'dexie';
import type { OutboxStatus, SyncEntity } from '@senm/domain';

export interface LocalStudent {
  id: string;
  code: string;
  fullName: string;
  searchName: string;
  active: boolean;
  cycleId: string;
  gradeId: string;
  gradeName: string;
  sectionId: string;
  sectionName: string;
  outstandingMinor: number;
  creditMinor: number;
  currency: string;
  version: number;
  updatedAt: string;
}

export interface LocalCharge {
  id: string;
  studentId: string;
  cycleId: string;
  conceptId: string;
  conceptName: string;
  conceptActive: boolean;
  amountMinor: number;
  appliedMinor: number;
  currency: string;
  dueDate: string | null;
  voided: boolean;
}

export interface LocalPayment {
  id: string;
  studentId: string;
  studentCode: string;
  studentName: string;
  cycleId: string;
  /** Nombre legible del ciclo. El recibo lo muestra; el identificador no sirve. */
  cycleName?: string;
  gradeName: string;
  sectionName: string;
  amountMinor: number;
  currency: string;
  method: string;
  reference: string | null;
  allocations: {
    chargeId: string;
    conceptId: string;
    conceptName: string;
    /** «Julio 2026» si la línea es una mensualidad. */
    monthLabel?: string | null;
    amountMinor: number;
    /** Lo rebajado en ESTA línea por el descuento de pago anual. */
    discountMinor?: number | null;
    balanceBeforeMinor: number;
    balanceAfterMinor: number;
  }[];
  creditMinor: number;
  status: string;
  effectiveAt: string;
  capturedAt: string;
  operationalDate: string;
  userId: string;
  userName: string;
  installationId: string;
  note: string | null;
  balanceBeforeMinor: number;
  balanceAfterMinor: number;
  receiptNumber: string | null;
  /** Total rebajado y por qué, para el pie del recibo. Sin descuento, ausente. */
  discountMinor?: number | null;
  discountReason?: string | null;
  /** Mensaje comprensible cuando el servidor lo rechazo o lo mando a revision. */
  statusMessage: string | null;
}

export interface LocalOutboxEntry {
  id: string;
  type: 'PAGO' | 'ANULACION' | 'REIMPRESION' | 'ESTUDIANTE_ALTA' | 'ESTUDIANTE_EDICION' | 'ESTUDIANTE_ESTADO';
  idempotencyKey: string;
  payload: unknown;
  status: OutboxStatus;
  attempts: number;
  createdAt: string;
  updatedAt: string;
  nextAttemptAt: string | null;
  dependsOn: string[];
  lastErrorCode: string | null;
  lastErrorMessage: string | null;
}

export interface LocalCursor {
  entity: SyncEntity | 'global';
  cursor: string | null;
  lastSyncedAt: string | null;
}

export interface LocalMeta {
  key: string;
  value: unknown;
}

export interface LocalConcept {
  id: string;
  code: string;
  name: string;
  active: boolean;
}

class SenmDatabase extends Dexie {
  students!: EntityTable<LocalStudent, 'id'>;
  charges!: EntityTable<LocalCharge, 'id'>;
  payments!: EntityTable<LocalPayment, 'id'>;
  outbox!: EntityTable<LocalOutboxEntry, 'id'>;
  cursors!: EntityTable<LocalCursor, 'entity'>;
  concepts!: EntityTable<LocalConcept, 'id'>;
  meta!: EntityTable<LocalMeta, 'key'>;

  constructor() {
    super('senm-pagos');

    // --- Version 1: esquema inicial ---------------------------------------
    this.version(1).stores({
      students: 'id, searchName, code, gradeId, sectionId, cycleId, outstandingMinor',
      charges: 'id, studentId, [studentId+cycleId], conceptId',
      payments: 'id, studentId, status, operationalDate, capturedAt',
      outbox: 'id, status, createdAt, nextAttemptAt',
      cursors: 'entity',
      concepts: 'id',
      meta: 'key',
    });

    // Ejemplo de migracion incremental segura: se agrega un indice sin tocar datos.
    // Dexie ejecuta las versiones en orden al abrir la base, y el `upgrade` puede
    // rellenar campos nuevos. Nunca se borra una tabla con operaciones pendientes.
    this.version(2)
      .stores({
        payments: 'id, studentId, status, operationalDate, capturedAt, receiptNumber',
      })
      .upgrade(async (transaction) => {
        await transaction
          .table<LocalPayment>('payments')
          .toCollection()
          .modify((payment) => {
            payment.statusMessage ??= null;
          });
      });
  }
}

export const db = new SenmDatabase();

/** Normaliza igual que el servidor, para que la busqueda local dé los mismos resultados. */
export function normalizeForSearch(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toUpperCase()
    .trim();
}

export async function getMeta<T>(key: string, fallback: T): Promise<T> {
  const row = await db.meta.get(key);
  return row === undefined ? fallback : (row.value as T);
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value });
}

/**
 * Borra TODOS los datos locales. Se ejecuta al cerrar definitivamente la sesion, al
 * revocarse el dispositivo o en una limpieza administrativa autorizada.
 *
 * Es una minimizacion real de datos personales: el dispositivo no conserva nada del
 * estudiantado despues de esto.
 */
export async function wipeLocalData(): Promise<void> {
  // Dexie acepta hasta 5 tablas como argumentos sueltos; con más se pasa un array.
  await db.transaction('rw', [db.students, db.charges, db.payments, db.outbox, db.cursors, db.concepts, db.meta], async () => {
    await Promise.all([
      db.students.clear(),
      db.charges.clear(),
      db.payments.clear(),
      db.outbox.clear(),
      db.cursors.clear(),
      db.concepts.clear(),
      db.meta.clear(),
    ]);
  });
}

/**
 * `true` si quedan operaciones sin sincronizar. Se consulta ANTES de permitir un
 * cierre de sesion destructivo, para no borrar dinero que aun no llegó al servidor.
 */
export async function hasUnsyncedWork(): Promise<boolean> {
  const pending = await db.outbox.where('status').anyOf('PENDIENTE', 'PROCESANDO', 'CONFLICTO').count();
  return pending > 0;
}
